import { isPublicPath } from '@/lib/publicPaths';

/**
 * Mid-session expiry (sweep 2026-09-15). The portal issues absolute 8-hour
 * sessions; when one ends under an open tab, every /api/v1 call answers 401.
 * The bootstrap /me probe used to be the only thing that acted on a 401, so
 * the tab kept polling a dead session — 80 field-board/status 401s in 13
 * minutes on one screen, a sleeping device all night on another — and in-SPA
 * navigation rendered broken pages until someone hard-reloaded.
 *
 * The API client reports every 401 here; AuthContext listens, signs the tab
 * out once (which stops the pollers) and hands off to the portal login with
 * the page to come back to. A tiny bus rather than an import of AuthContext
 * from the client, which would be a cycle.
 */
type Listener = () => void;

const listeners = new Set<Listener>();

/** Subscribe to "a signed-in request was refused as unauthenticated". */
export function onSessionExpired(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * Called by the API client for every 401. Only same-origin /api/v1 requests
 * count (not the Sanctum CSRF fetch), and nothing is reported while the tab
 * is on a public page — those render with no portal session by design.
 */
export function reportUnauthenticated(url: string | undefined): void {
  if (!url || !url.startsWith('/api/v1/')) return;
  if (isPublicPath(window.location.pathname)) return;
  listeners.forEach((l) => l());
}
